import { styled, css } from 'styled-components';
import { LinkButton, Text, useClipboard, useThemeColor, useToast, Tooltip } from '@wistia/vhs';
import { useNavigate, useParams } from 'react-router';
import { paths } from '~/singlePageApp/paths';
import { isNotNil } from '~/utilities/type-guards';
import type { PublicCommentsSelection } from '~/graphql';
import type { PropsWithChildren, JSX } from 'react';
import { trackStructEvent } from '~/utilities/snowplow';
import Select from 'react-select';
import { useUpdatePublicCommentsSelectionMutation } from './__gql/Comments.gql.gen';

const ChooserContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  grid-area: Chooser;
  z-index: 2;
`;

const UnderlineStyle = css`
  text-decoration: underline dotted;
`;

const StyledLink = styled(LinkButton)`
  cursor: pointer;
  ${UnderlineStyle}
`;

const StyledText = styled.span`
  cursor: not-allowed;
  ${UnderlineStyle}
`;

// @ts-expect-error - React 19 compatibility issue with react-select styled-components
const StyledSelect = styled(Select)<{ isRightOfPlayer: boolean }>`
  width: ${({ isRightOfPlayer }: { isRightOfPlayer: boolean }) =>
    isRightOfPlayer ? '110px' : '140px'};
`;

const SELECT_OPTIONS = [
  { label: 'Team', value: 'team' },
  { label: 'Audience', value: 'audience' },
  { label: 'None', value: 'none' },
];

const CopyLink = ({ disabled, children }: PropsWithChildren<{ disabled: boolean }>) => {
  const [onCopy] = useClipboard(window.location.href);
  const showToast = useToast();

  const onClick = async () => {
    await onCopy();
    showToast({ message: 'Link to media page copied!' });
  };

  if (disabled) {
    return (
      <Tooltip label="Locked projects have no public media page">
        <StyledText>{children}</StyledText>
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Copy Link">
      <StyledLink
        noStyle={true}
        onClick={() => {
          void onClick();
        }}
      >
        {children}
      </StyledLink>
    </Tooltip>
  );
};

export const PublicCommentsChooser = ({
  mediaId,
  initialCommentSelection,
  projectIsLocked,
  isRightOfPlayer,
}: {
  mediaId: string;
  initialCommentSelection: PublicCommentsSelection;
  projectIsLocked: boolean;
  isRightOfPlayer: boolean;
}): JSX.Element => {
  const { mediaHashedId } = useParams();
  const grey700 = useThemeColor('grey700');

  const showToast = useToast();
  const navigate = useNavigate();

  const defaultValue = SELECT_OPTIONS.find(({ value }) => value === initialCommentSelection);

  const [updateCommentSelection] = useUpdatePublicCommentsSelectionMutation({
    onCompleted: () => {
      showToast({
        message: 'Public media page updated!',
        actionLabel: 'Preview',
        actionInterceptor: () => {
          if (isNotNil(mediaHashedId)) {
            navigate(paths.mediaPreview(mediaHashedId));
          }
        },
      });
    },
    onError: () => {
      showToast({
        message: 'Public media page failed to update.',
        variant: 'error',
      });
    },
  });

  const onChangeSelection = ({ value }: { value: PublicCommentsSelection }) => {
    trackStructEvent({
      category: 'input',
      action: 'change',
      label: 'media_comments_visibility_clicked',
      value,
    });

    void updateCommentSelection({
      variables: {
        input: {
          mediaId,
          publicCommentsSelection: value,
        },
      },
    });
  };

  return (
    <ChooserContainer>
      <Text
        colorOverride={grey700}
        variant="subtitle2"
        style={{ flexGrow: '0' }}
      >
        Show on <CopyLink disabled={projectIsLocked}>public media page:</CopyLink>
      </Text>
      <StyledSelect
        isRightOfPlayer={isRightOfPlayer}
        aria-label="Public Comments Chooser"
        components={{
          IndicatorSeparator: null,
        }}
        isSearchable={false}
        options={SELECT_OPTIONS}
        onChange={onChangeSelection}
        placeholder={null}
        defaultValue={defaultValue}
      />
    </ChooserContainer>
  );
};
